@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=JetBrains+Mono:wght@400;500&family=Lora:ital,wght@0,400;0,500;1,400&display=swap');
@import "tailwindcss";
@import "./theme.css";

@variant dark (&:where(.dark, .dark *));

@theme {
  --color-midnight: #0F1923;
  --color-graphite: #1B2A3E;
  --color-signal: #2E8BF0;
  --color-cyan: #00D4FF;
  --color-ice: #F8FAFE;
}

@layer base {
  :root {
    --color-primary: #111827;
    --color-primary-light: #1f2937;
    --color-accent: #2E8BF0;
    --color-accent-light: #3b82f6;
    --accent-primary: #2E8BF0;
    --accent-light: rgba(46, 139, 240, 0.12);
    --accent-ring: rgba(46, 139, 240, 0.35);
    /* Design system tokens */
    --wm-radius-sm:            6px;
    --wm-radius-md:            8px;
    --wm-radius-lg:            12px;
    --wm-card-px:              1.25rem;
    --wm-card-py:              0.875rem;
    --wm-row-py:               0.5rem;
    --wm-list-gap:             0.625rem;
    --wm-card-shadow:          none;
    --wm-card-border-width:    1px;
    --wm-card-border-color:    #e5e7eb;
  }

  /* ── WrapMind Type Scale ─────────────────────────────────────────────────
     ALL sizes are rem — they scale automatically when the user changes
     Text Size in Appearance Settings (Tiny 11px · Small 12px · Default 14px
     · Large 16px · X-Large 18px).

     ╔═══════════════════╦══════════════════════════════════╦════════════════════════╗
     ║ Token             ║ Role                             ║ px @ 11/12/14/16/18px  ║
     ╠═══════════════════╬══════════════════════════════════╬════════════════════════╣
     ║ --wm-text-hero    ║ KPI numbers, page titles         ║  14 / 15 / 18 / 21 / 23 ║
     ║ --wm-text-heading ║ Section headers, card titles     ║  13 / 14 / 16 / 18 / 21 ║
     ║ --wm-text-cta     ║ Primary buttons, key actions     ║  11 / 12 / 14 / 16 / 18 ║
     ║ --wm-text-body    ║ Body copy, table rows, forms     ║  10 / 11 / 13 / 15 / 17 ║
     ║ --wm-text-ui      ║ Nav labels, field labels, btns   ║   9 / 10 / 12 / 14 / 15 ║
     ║ --wm-text-meta    ║ Dates, status text, help text    ║   8 /  9 / 11 / 13 / 14 ║
     ║ --wm-text-caption ║ Badges, chart labels, tooltips   ║   8 /  9 / 10 / 11 / 13 ║
     ║ --wm-text-micro   ║ Chart axis ticks — rarely        ║   7 /  8 /  9 / 10 / 12 ║
     ╚═══════════════════╩══════════════════════════════════╩════════════════════════╝

     RULE: NEVER use Tailwind's text-xs / text-sm / text-base / text-lg
     or hardcoded text-[Npx] in any component. Always use a semantic token.
     The only exception: prose content that intentionally ignores user prefs. */

  /* Primitive size tokens — referenced by semantic tokens below */
  --wm-t-8:  0.571rem;
  --wm-t-9:  0.643rem;
  --wm-t-10: 0.714rem;
  --wm-t-11: 0.786rem;
  --wm-t-12: 0.857rem;
  --wm-t-13: 0.929rem;
  --wm-t-15: 1.071rem;
  --wm-t-16: 1.143rem;
  --wm-t-17: 1.214rem;
  --wm-t-18: 1.286rem;

  /* ── Semantic type tokens — USE THESE in all components ─────────────── */
  --wm-text-hero:    var(--wm-t-18);   /* 18px @ default */
  --wm-text-heading: var(--wm-t-16);   /* 16px @ default */
  --wm-text-cta:     1rem;             /* 14px @ default — scales 1:1 with base */
  --wm-text-body:    var(--wm-t-13);   /* 13px @ default */
  --wm-text-ui:      var(--wm-t-12);   /* 12px @ default */
  --wm-text-meta:    var(--wm-t-11);   /* 11px @ default */
  --wm-text-caption: var(--wm-t-10);   /* 10px @ default */
  --wm-text-micro:   var(--wm-t-9);    /*  9px @ default */

  * {
    -webkit-tap-highlight-color: transparent;
  }

  html {
    touch-action: manipulation;
    /* Font size is driven by --wm-font-size-base set by ThemeContext */
    font-size: var(--wm-font-size-base, 14px);
  }

  body {
    margin: 0;
    padding-top: 0;
    /* Font family driven by --wm-font-family set by ThemeContext */
    font-family: var(--wm-font-family, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overscroll-behavior: none;
    /* Background driven by --wm-bg-primary set by ThemeContext */
    background: var(--wm-bg-primary, #0F1923);
    color: var(--text-primary, #F8FAFE);
    font-size: var(--wm-font-size-base, 14px);
  }

  .font-mono {
    font-family: 'JetBrains Mono', 'Courier New', monospace !important;
  }

  /* 16px minimum on text inputs/textareas prevents iOS auto-zoom on focus */
  input, textarea {
    font-size: max(16px, var(--wm-font-size-base, 14px)) !important;
  }
  /* Selects use the UI's standard small scale — NOT the 16px iOS rule,
     because selects open a native picker on iOS (no zoom occurs).
     Individual components can override via text-xs / text-sm Tailwind classes. */
  select {
    font-size: 0.75rem; /* 12px — text-xs, matches the rest of the UI */
  }
  /* Placeholder text is smaller than input text — explicitly set on ::placeholder
     so the 16px !important on the element doesn't get inherited here */
  input::placeholder, textarea::placeholder {
    font-size: 0.75rem; /* 12px — text-xs, matches label scale throughout the UI */
    color: #94A3B8;
  }
  .dark input::placeholder,
  .dark textarea::placeholder {
    color: #4A6380;
  }
  button {
    font-size: inherit;
  }
}


.dark {
  color-scheme: dark;
}

.safe-bottom {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ── Nav theme CSS variables — light mode defaults, dark overrides ─────── */
:root {
  --wm-nav-bg:          #ffffff;
  --wm-nav-border:      #e5e7eb;
  --wm-nav-text:        #64748B;
  --wm-nav-text-active: #0F1923;
  --wm-nav-hover-bg:    rgba(0,0,0,0.05);
  --wm-nav-active-bg:   rgba(46,139,240,0.10);
  --wm-nav-accent:      #2E8BF0;
}

.dark {
  --wm-nav-bg:          #1B2A3E;
  --wm-nav-border:      #243348;
  --wm-nav-text:        #7D93AE;
  --wm-nav-text-active: #F8FAFE;
  --wm-nav-hover-bg:    rgba(36,51,72,0.9);
  --wm-nav-active-bg:   rgba(46,139,240,0.15);
  --wm-nav-accent:      #2E8BF0;
}

/* Nav item base + hover + active states via CSS vars */
.wm-nav-item {
  color: var(--wm-nav-text);
  border-radius: var(--wm-radius-sm, 0.375rem);
  transition: background-color 0.12s, color 0.12s;
}
.wm-nav-item:hover {
  background-color: var(--wm-nav-hover-bg);
  color: var(--wm-nav-text-active);
}
.wm-nav-active {
  background-color: var(--wm-nav-active-bg) !important;
  color: var(--wm-nav-accent) !important;
  font-weight: 500;
}

/* ── Theme-reactive button classes ─────────────────────────────────────── */
.wm-btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
  height: 1.75rem; padding: 0 0.75rem; border-radius: var(--wm-radius-sm, 0.375rem);
  background-color: var(--btn-primary-bg);
  color: var(--btn-primary-text, #fff);
  font-size: 0.75rem; font-weight: 500; white-space: nowrap;
  border: none; cursor: pointer; transition: background-color 0.15s, opacity 0.15s;
  flex-shrink: 0;
}
.wm-btn-primary:hover { background-color: var(--btn-primary-hover); }
.wm-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

.wm-btn-outline {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
  height: 1.75rem; padding: 0 0.75rem; border-radius: var(--wm-radius-sm, 0.375rem);
  background-color: transparent;
  color: var(--btn-outline-text);
  border: 1px solid var(--btn-outline-border);
  font-size: 0.75rem; font-weight: 500; white-space: nowrap;
  cursor: pointer; transition: background-color 0.15s, border-color 0.15s;
  flex-shrink: 0;
}
.wm-btn-outline:hover { background-color: var(--btn-outline-hover-bg); }
.wm-btn-outline:disabled { opacity: 0.45; cursor: not-allowed; }

.wm-btn-ghost {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
  height: 1.75rem; padding: 0 0.75rem; border-radius: var(--wm-radius-sm, 0.375rem);
  background-color: transparent; border: none;
  color: var(--text-muted, #64748B);
  font-size: 0.75rem; font-weight: 500; white-space: nowrap;
  cursor: pointer; transition: background-color 0.15s, color 0.15s;
  flex-shrink: 0;
}
.wm-btn-ghost:hover { background-color: var(--btn-ghost-hover-bg); color: var(--text-primary, #0F1923); }
.wm-btn-ghost:disabled { opacity: 0.45; cursor: not-allowed; }

.wm-btn-danger {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
  height: 1.75rem; padding: 0 0.75rem; border-radius: var(--wm-radius-sm, 0.375rem);
  background-color: var(--btn-danger-bg, #ef4444); color: #fff;
  font-size: 0.75rem; font-weight: 500; white-space: nowrap;
  border: none; cursor: pointer; transition: background-color 0.15s;
  flex-shrink: 0;
}
.wm-btn-danger:hover { background-color: var(--btn-danger-hover, #dc2626); }
.wm-btn-danger:disabled { opacity: 0.45; cursor: not-allowed; }

.wm-btn-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 2rem; height: 2rem; border-radius: var(--wm-radius-sm, 0.375rem);
  background-color: transparent; border: none;
  color: var(--text-muted, #64748B);
  cursor: pointer; transition: background-color 0.15s, color 0.15s;
  flex-shrink: 0;
}
.wm-btn-icon:hover { background-color: var(--btn-ghost-hover-bg); color: var(--text-primary, #0F1923); }
.wm-btn-icon:disabled { opacity: 0.45; cursor: not-allowed; }

/* Size modifiers */
.wm-btn-sm { height: 1.5rem !important; padding: 0 0.5rem !important; font-size: 0.6875rem !important; }
.wm-btn-lg { height: 2.25rem !important; padding: 0 1rem !important; font-size: 0.8125rem !important; }
.wm-btn-icon-sm { width: 1.5rem !important; height: 1.5rem !important; }

/* ── .wm-input utility — mirrors canonical input style in plain CSS ───────── */
.wm-input {
  width: 100%;
  height: 2rem; /* h-8 */
  padding: 0 0.75rem; /* px-3 */
  border-radius: 0.25rem;
  border: 1px solid #E5E7EB;
  background: #ffffff;
  color: #0F1923;
  font-size: 0.875rem; /* text-sm */
  transition: border-color 0.15s, box-shadow 0.15s;
}
.wm-input::placeholder {
  font-size: 0.75rem; /* text-xs */
  color: #94A3B8;
}
.wm-input:focus {
  outline: none;
  border-color: #2E8BF0;
  box-shadow: 0 0 0 2px rgba(46, 139, 240, 0.2);
}
.dark .wm-input {
  border-color: #243348;
  background: #0F1923;
  color: #F8FAFE;
}
.dark .wm-input::placeholder {
  color: #4A6380;
}

/* Input focus token */
input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--accent-primary) !important;
  box-shadow: 0 0 0 2px var(--accent-ring) !important;
}

/* ── KPI signal animations — subtle glow for major movement (≥15% delta) ── */
@keyframes kpi-surge {
  0%, 100% { box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }
  50%       { box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05), 0 0 0 3px rgba(16,185,129,0.18), 0 0 16px 0 rgba(16,185,129,0.07); }
}
@keyframes kpi-drop {
  0%, 100% { box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }
  50%       { box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05), 0 0 0 3px rgba(239,68,68,0.18), 0 0 16px 0 rgba(239,68,68,0.07); }
}
.kpi-surge { animation: kpi-surge 4s ease-in-out infinite; }
.kpi-drop  { animation: kpi-drop  4s ease-in-out infinite; }

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-pulse-badge {
  animation: pulse-badge 1.5s ease-in-out infinite;
}

@keyframes waveBar {
  from { transform: scaleY(0.4); opacity: 0.5; }
  to   { transform: scaleY(1.0); opacity: 1;   }
}

/* ── Site-wide animation keyframes ─────────────────────────────────────── */

@keyframes accent-glow {
  0%, 100% { box-shadow: 0 0 6px 1px var(--accent-ring); }
  50%       { box-shadow: 0 0 18px 5px var(--accent-ring); }
}

@keyframes logo-breathe {
  0%, 100% { box-shadow: 0 0 0px 0px rgba(46,139,240,0); opacity: 0.9; }
  50%       { box-shadow: 0 0 10px 4px rgba(46,139,240,0.35); opacity: 1; }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes wm-page-enter {
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes scale-in {
  from { opacity: 0; transform: scale(0.88); }
  to   { opacity: 1; transform: scale(1); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

@keyframes ticker-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

@keyframes pulse-ring {
  0%   { transform: scale(1);   opacity: 0.8; }
  100% { transform: scale(2.2); opacity: 0; }
}

/* Feedback widget nudge — 4 pulses then stops */
@keyframes wm-nudge-ring {
  0%   { transform: scale(1);   opacity: 0.7; }
  100% { transform: scale(2.6); opacity: 0;   }
}
.wm-nudge-ring::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: inherit;
  background: var(--accent-primary);
  animation: wm-nudge-ring 1.4s ease-out 4;
  pointer-events: none;
  z-index: 0;
}

@keyframes gradient-x {
  0%, 100% { background-position: 0% 50%; }
  50%       { background-position: 100% 50%; }
}

/* ── Animation utility classes ──────────────────────────────────────────── */

.wm-fade-in-up   { animation: fade-in-up 0.35s ease-out both; }
.wm-page-enter   { animation: wm-page-enter 0.18s cubic-bezier(0.16,1,0.3,1) both; }
.wm-scale-in     { animation: scale-in  0.22s ease-out both; }
.wm-float        { animation: float     3.5s ease-in-out infinite; }
.wm-spin-slow    { animation: spin-slow 18s  linear infinite; }
.wm-glow-accent  { animation: accent-glow 2.8s ease-in-out infinite; }
.wm-logo-breathe { animation: logo-breathe 3s ease-in-out infinite; }

/* Staggered entrance for list children — apply to parent, children auto-stagger */
.wm-stagger > * { animation: fade-in-up 0.35s ease-out both; }
.wm-stagger > *:nth-child(1)  { animation-delay: 0.03s; }
.wm-stagger > *:nth-child(2)  { animation-delay: 0.06s; }
.wm-stagger > *:nth-child(3)  { animation-delay: 0.09s; }
.wm-stagger > *:nth-child(4)  { animation-delay: 0.12s; }
.wm-stagger > *:nth-child(5)  { animation-delay: 0.15s; }
.wm-stagger > *:nth-child(6)  { animation-delay: 0.18s; }
.wm-stagger > *:nth-child(7)  { animation-delay: 0.21s; }
.wm-stagger > *:nth-child(8)  { animation-delay: 0.24s; }
.wm-stagger > *:nth-child(9)  { animation-delay: 0.27s; }
.wm-stagger > *:nth-child(10) { animation-delay: 0.30s; }

/* Shimmer skeleton loader */
.wm-shimmer {
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
  background-size: 400px 100%;
  animation: shimmer 1.6s ease-in-out infinite;
}

/* Hover card lift + glow */
.wm-card-hover {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.wm-card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px -4px rgba(0,0,0,0.35), 0 0 0 1px var(--accent-ring);
}

/* New Estimate button glow */
.wm-btn-cta-glow {
  animation: accent-glow 2.8s ease-in-out infinite;
}

/* Active nav item glow — animation removed per design spec; .wm-nav-active provides bg+color */
.wm-nav-active-glow {
  /* intentionally empty */
}

/* Settings button — simple glow on hover, no spin */
.wm-settings-btn { transition: box-shadow 0.2s ease, opacity 0.2s ease; }
.wm-settings-btn:hover {
  box-shadow: 0 0 8px 2px color-mix(in srgb, var(--wm-nav-accent) 45%, transparent);
}

/* Card style — controlled by CSS vars set in ThemeContext */
.wm-card {
  border-radius: var(--wm-radius-md, 8px);
  box-shadow: var(--wm-card-shadow, none);
  border-width: var(--wm-card-border-width, 1px);
  border-style: solid;
  border-color: var(--wm-card-border-color, #e5e7eb);
}

.wm-card-header {
  padding: var(--wm-card-py, 0.875rem) var(--wm-card-px, 1.25rem);
}

.wm-card-body {
  padding: var(--wm-card-py, 0.875rem) var(--wm-card-px, 1.25rem);
}

/* ── High contrast mode ─────────────────────────────────────────────────── */
.wm-high-contrast {
  filter: contrast(1.35);
}
.wm-high-contrast .wm-card {
  --wm-card-border-width: 2px !important;
}
.wm-high-contrast .wm-nav-item:hover {
  outline: 2px solid var(--wm-nav-accent);
  outline-offset: -2px;
}
.wm-high-contrast input,
.wm-high-contrast select,
.wm-high-contrast textarea {
  outline: 2px solid var(--accent-primary);
}

/* ── Reduce motion — toggled via .wm-reduce-motion class on <html> ───── */
.wm-reduce-motion *,
.wm-reduce-motion *::before,
.wm-reduce-motion *::after {
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
  scroll-behavior: auto !important;
}

/* Pulse ring for online dot */
.wm-pulse-ring::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: #22c55e;
  animation: pulse-ring 1.8s ease-out infinite;
}

/* Ticker strip */
.wm-ticker-wrap {
  overflow: hidden;
  white-space: nowrap;
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%);
  mask-image: linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%);
}
.wm-ticker-track {
  display: inline-flex;
  animation: ticker-scroll 38s linear infinite;
}
.wm-ticker-track:hover { animation-play-state: paused; }

/* Gradient animated background (for hero stat areas) */
.wm-gradient-animate {
  background-size: 200% 200%;
  animation: gradient-x 6s ease infinite;
}

/* ── Toast slide-in animation ──────────────────────────────────────────────── */
@keyframes wm-toast-in {
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: translateX(0); }
}
.wm-toast-in {
  animation: wm-toast-in 0.18s cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* ── Liquid glass orb backdrop ───────────────────────────────────────────── */
/* These color blobs float fixed behind the entire app. Elements with
   backdrop-filter blur them, producing genuine iOS-style frosted glass.    */

:root {
  --wm-orb-1: rgba(46, 139, 240, 0.22);   /* brand blue  */
  --wm-orb-2: rgba(99, 102, 241, 0.18);   /* indigo      */
  --wm-orb-3: rgba(0,  212, 255, 0.14);   /* cyan accent */
}
.dark {
  --wm-orb-1: rgba(46, 139, 240, 0.40);
  --wm-orb-2: rgba(99, 102, 241, 0.32);
  --wm-orb-3: rgba(0,  212, 255, 0.20);
}

@keyframes wm-orb-drift-1 {
  0%,  100% { transform: translate(0px,    0px)   scale(1);    }
  33%        { transform: translate(90px,  -70px)  scale(1.07); }
  66%        { transform: translate(-50px,  80px)  scale(0.93); }
}
@keyframes wm-orb-drift-2 {
  0%,  100% { transform: translate(0px,    0px)   scale(1);    }
  40%        { transform: translate(-80px,  90px)  scale(0.91); }
  72%        { transform: translate(60px,  -50px)  scale(1.09); }
}
@keyframes wm-orb-drift-3 {
  0%,  100% { transform: translate(0px,    0px)   scale(1);    }
  50%        { transform: translate(50px,   60px)  scale(1.05); }
}

/* ── Glass morphism utilities ────────────────────────────────────────────── */

/* Semi-transparent glass bg — orbs bleed through these surfaces */
:root  { --wm-glass-bg: rgba(248, 250, 254, 0.62); --wm-glass-bg-70: rgba(248, 250, 254, 0.85); }
.dark  { --wm-glass-bg: rgba(13,  21,  35,  0.60); --wm-glass-bg-70: rgba(13,  21,  35,  0.85); }

/* Glass border / specular highlight */
:root  { --wm-glass-border:    rgba(255,255,255,0.60); }
.dark  { --wm-glass-border:    rgba(255,255,255,0.09); }
:root  { --wm-glass-specular:  rgba(255,255,255,0.80); }
.dark  { --wm-glass-specular:  rgba(255,255,255,0.12); }

.wm-glass {
  backdrop-filter: blur(32px) saturate(160%);
  -webkit-backdrop-filter: blur(32px) saturate(160%);
}
.wm-glass-subtle {
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
}
.wm-glass-heavy {
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
}

/* Glass shimmer — CSS-only frosted look for elements with solid backgrounds
   (no backdrop blur needed). Diagonal light-streak suggests glass material. */
.wm-glass-shimmer::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    135deg,
    rgba(255,255,255,0.10) 0%,
    rgba(255,255,255,0.02) 40%,
    rgba(255,255,255,0.07) 70%,
    rgba(255,255,255,0.01) 100%
  );
  border-radius: inherit;
  z-index: 0;
}
